import React, { Fragment, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardContent, CardHeader, Divider, Typography } from '@material-ui/core'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import * as d3 from 'd3'
import PercentageMetricLoading from './PercentageMetricLoading'

const useStyles = makeStyles({
  card: {
    height: '100%',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  cardHeaderTitle: {
    fontSize: 16,
  },
  chartContainer: {
    width: 100,
    height: 100,
  },
  subMetricContainer: {
    display: 'flex',
  },
  subMetric: {
    fontWeight: 700,
  },
  subMetricLabel: {
    fontWeight: 'normal',
    textAlign: 'center',
  },
})

const PercentageMetric = ({ title, subheader, metric, subMetric, subMetricLabel, loading, error }) => {
  const container = useRef()
  const theme = useTheme()
  const styles = useStyles()
  const formattedMetric = metric ? metric.toFixed() : 0
  const formattedSubMetric = subMetric ? subMetric.toFixed() : 0

  useEffect(() => {
    if (loading) {
      return
    }
    const svgContainer = d3.select(container.current).node()
    const width = svgContainer.getBoundingClientRect().width
    const height = width
    const radius = Math.min(width, height) / 2
    const innerRadius = radius * 0.75
    const arcColor = '#0B79D0'
    const backgroundRingColor = theme.palette.grey[200]
    const fontColor = theme.palette.text.primary
    const fontFamily = theme.typography.fontFamily

    const svg = d3
      .select(container.current)
      .select('#svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .select('#metric-grouping')
      .attr('transform', `translate(${radius}, ${radius})`)

    // Grey ring
    svg
      .select('#background-circle')
      .attr('d', d3.arc().innerRadius(innerRadius).outerRadius(radius).startAngle(0).endAngle(360))
      .attr('fill', backgroundRingColor)

    // Animate Metric Arc
    const metricAsRatio = metric / 100

    svg
      .select('#metric-arc')
      .attr('fill', arcColor)
      .transition()
      .duration(1000)
      .attrTween('d', () => {
        const startAngle = (4 / 3) * Math.PI
        const endAngle = 2 * Math.PI * metricAsRatio + startAngle
        const interpolate = d3.interpolate(startAngle, endAngle)
        const arc = d3
          .arc()
          .innerRadius(innerRadius)
          .outerRadius(radius)
          .cornerRadius(50)
          .startAngle(startAngle)
          .endAngle(startAngle)

        return (t) => {
          arc.endAngle(interpolate(t))
          return arc()
        }
      })

    // Set Metric Text
    if (!error) {
      svg
        .select('#metric-text')
        .text(`${formattedMetric}%`)
        .attr('y', 10)
        .style('text-anchor', 'middle')
        .style('fill', fontColor)
        .style('font-family', fontFamily)
        .style('font-size', '24px')
    }
  }, [loading, formattedMetric, metric, theme, error])

  return (
    <Card
      elevation={0}
      className={styles.card}
    >
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'body1', color: 'textPrimary', className: styles.cardHeaderTitle }}
        subheader={subheader}
        subheaderTypographyProps={{ variant: 'body2', color: 'textSecondary' }}
      />
      <Divider light />
      <CardContent className={styles.cardContent}>
        {loading ? (
          <PercentageMetricLoading loading={loading} />
        ) : (
          <Fragment>
            <div
              className={styles.chartContainer}
              ref={container}
            >
              <svg id="svg">
                <g id="metric-grouping">
                  <path id="background-circle" />
                  <path id="metric-arc" />
                  <text id="metric-text" />
                </g>
              </svg>
            </div>
            <Box
              className={styles.subMetricContainer}
              mt={2}
            >
              <Typography
                className={styles.subMetric}
                variant="caption"
                color="textSecondary"
                align="center"
              >
                {`${formattedSubMetric}% of all Scoir Students`}
                <span className={styles.subMetricLabel}>&nbsp;{subMetricLabel}</span>
              </Typography>
            </Box>
          </Fragment>
        )}
      </CardContent>
    </Card>
  )
}

PercentageMetric.propTypes = {
  title: PropTypes.string.isRequired,
  subheader: PropTypes.string.isRequired,
  metric: PropTypes.number.isRequired,
  subMetric: PropTypes.number.isRequired,
  subMetricLabel: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
}

export default PercentageMetric
